import { AppColors } from "@/styles/colors";
import { formatCurrency } from "@/utils/format-currency";
import {
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Check, Handshake, PenLine, X } from "lucide-react";

import "@/components/common/section-components/card/text-elements.css";
import { SellOrderDetail } from "@/pages/grain-requests/sell-orders/schema/sell-order.schema";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { Control, FieldValues } from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { SellOrderStatus } from "@/constants/entity-status";

interface Props {
  sellOrder?: SellOrderDetail | null;
  isEditingAmount?: boolean;
  loadingEditAmount?: boolean;
  handleEditAmount?: () => void;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  errors?: any;
  control: Control<FieldValues>;
  loadingSellOrder?: boolean;
  loadingExecuteSellOrder?: boolean;
  loadingRejectSellOrder?: boolean;
  loadingSendNegotiation?: boolean;
  loadingSendCommunication?: boolean;
  handleExecuteSellOrder?: () => void;
  handleSendNegotiation?: () => void;
  setOpenCommunicationModal?: (open: boolean) => void;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  clearErrors: (name?: string | string[] | undefined) => void;
  setValue: (name: string, value: any) => void;
}

export const SellOrderAmountAndActions = ({
  sellOrder,
  isEditingAmount,
  loadingEditAmount,
  handleEditAmount,
  errors,
  control,
  setIsEditingAmount,
  loadingSellOrder,
  loadingExecuteSellOrder,
  loadingRejectSellOrder,
  loadingSendNegotiation,
  loadingSendCommunication,
  handleExecuteSellOrder,
  handleSendNegotiation,
  setOpenCommunicationModal,
  setOpenRejectModal,
  clearErrors,
  setValue,
}: Props) => {
  const { userPermissions } = useRoles();

  const cantOperateSellOrder =
    sellOrder?.estado == SellOrderStatus.FINISHED ||
    sellOrder?.estado == SellOrderStatus.REJECTED ||
    sellOrder?.estado == SellOrderStatus.CONFIRMED;

  const negotiateDisabled =
    loadingSellOrder ||
    loadingRejectSellOrder ||
    loadingSendCommunication ||
    loadingSendNegotiation ||
    loadingExecuteSellOrder ||
    cantOperateSellOrder ||
    sellOrder?.estado === SellOrderStatus.NEGOTIATION;

  const communicationDisabled =
    loadingSellOrder ||
    loadingRejectSellOrder ||
    loadingSendCommunication ||
    loadingSendNegotiation ||
    loadingExecuteSellOrder ||
    cantOperateSellOrder ||
    (sellOrder?.estado !== SellOrderStatus.NEGOTIATION &&
      sellOrder?.estado !== SellOrderStatus.IN_COMMUNICATION);

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.GRAIN_SELL_ORDER && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex justifyContent="space-between">
      <Grid templateColumns="repeat(3, 1fr)" gap="36px">
        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              textTransform="uppercase"
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
            >
              Precio
            </Text>
            <Flex alignItems="center" gap="10px">
              {/* Cambiar a input al hacer click */}
              {isEditingAmount ? (
                <InputCurrency
                  control={control}
                  name="sell_order_amount"
                  defaultValue={String(sellOrder?.montoSolicitado)}
                  data-testid="sell-order-amount-input"
                  maxLength={20}
                  hasError={Boolean(errors?.sell_order_amount)}
                />
              ) : (
                <Flex alignItems="center" gap="8px">
                  <Text
                    fontSize={{
                      base: "15px",
                      sm: "15px",
                      md: "15px",
                      lg: "18px",
                      xl: "20px",
                    }}
                    fontWeight={700}
                    cursor={
                      sellOrder?.sinBase || !hasWriteAccess
                        ? "default"
                        : "pointer"
                    }
                  >
                    {sellOrder?.sinBase
                      ? "Sin base"
                      : `$${formatCurrency(sellOrder?.montoSolicitado)}`}
                  </Text>

                  {!cantOperateSellOrder && hasWriteAccess && (
                    <PenLine
                      data-testid="cash-pen-line"
                      cursor="pointer"
                      onClick={() => {
                        setIsEditingAmount(true);
                        setValue(
                          "sell_order_amount",
                          String(sellOrder?.montoSolicitado),
                        );
                      }}
                    />
                  )}
                </Flex>
              )}
              {loadingEditAmount && isEditingAmount && (
                <Spinner data-testid="cash-sell-order-edit-amount-spinner" />
              )}
              {isEditingAmount && !loadingEditAmount && (
                <Check
                  cursor={errors?.sell_order_amount ? "not-allowed" : "pointer"}
                  onClick={handleEditAmount}
                  data-testid="sell-order-edit-amount-button"
                />
              )}
              {isEditingAmount && (
                <X
                  data-testid="sell-order-cancel-edit-button"
                  cursor="pointer"
                  onClick={() => {
                    setIsEditingAmount(false);
                    clearErrors("sell_order_amount");
                  }}
                />
              )}
            </Flex>
            {errors?.sell_order_amount && (
              <Text color={AppColors.ERROR_TEXT}>
                {errors?.sell_order_amount?.message as string}
              </Text>
            )}
          </Flex>
        </GridItem>
        {Boolean(sellOrder?.precioFinal) &&
          sellOrder?.estado == SellOrderStatus.CONFIRMED && (
            <GridItem>
              <Flex
                minH={"82px"}
                gap="10px"
                padding="12px"
                flexDir="column"
                borderRadius="12px"
                border={`1px solid ${AppColors.WHITE_BORDER_1}`}
              >
                <Text
                  fontWeight={700}
                  textTransform="uppercase"
                  fontSize="12px"
                  color={AppColors.GRAY_TEXT}
                >
                  Precio final
                </Text>
                <Flex alignItems="center" gap="8px">
                  <Text
                    fontSize={{
                      base: "15px",
                      sm: "15px",
                      md: "15px",
                      lg: "18px",
                      xl: "20px",
                    }}
                    fontWeight={700}
                  >
                    {`$${formatCurrency(sellOrder?.precioFinal)}`}
                  </Text>
                </Flex>
              </Flex>
            </GridItem>
          )}

        <GridItem>
          <Flex
            minH={"82px"}
            gap="10px"
            padding="12px"
            flexDir="column"
            borderRadius="12px"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
          >
            <Text
              fontWeight={700}
              fontSize="12px"
              color={AppColors.GRAY_TEXT}
              textTransform={"uppercase"}
            >
              Toneladas
            </Text>
            <Flex alignItems="center" gap="10px">
              <Text
                fontSize={{
                  base: "15px",
                  sm: "15px",
                  md: "15px",
                  lg: "18px",
                  xl: "20px",
                }}
                fontWeight={700}
              >
                {sellOrder?.toneladas
                  ? `${Number(sellOrder?.toneladas).toLocaleString("es-AR")}`
                  : "-"}
              </Text>
            </Flex>
          </Flex>
        </GridItem>
      </Grid>
      {hasWriteAccess && !cantOperateSellOrder && (
        <HStack>
          {sellOrder?.estado !== SellOrderStatus.IN_PROCESS && (
            <Button
              disabled={
                loadingSellOrder ||
                loadingRejectSellOrder ||
                loadingSendNegotiation ||
                loadingSendCommunication ||
                loadingExecuteSellOrder ||
                cantOperateSellOrder
              }
              paddingX="12px"
              paddingY="4px"
              bgColor={AppColors.LIGHT_GREEN}
              onClick={handleExecuteSellOrder}
              loading={loadingExecuteSellOrder}
              data-testid="sell-order-execute-btn"
            >
              <Check />
              <Text
                fontWeight={600}
                fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
              >
                Confirmar
              </Text>
            </Button>
          )}

          {(sellOrder?.estado === SellOrderStatus.NEGOTIATION ||
            sellOrder?.estado === SellOrderStatus.IN_COMMUNICATION) && (
            <Button
              paddingX="12px"
              paddingY="4px"
              bgColor={AppColors.BLACK_TEXT_SECONDARY}
              disabled={communicationDisabled}
              onClick={() => setOpenCommunicationModal?.(true)}
              loading={loadingSendCommunication}
              data-testid="sell-order-communication-btn"
            >
              <Handshake />
              <Text
                fontWeight={600}
                fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
              >
                Comunicación
              </Text>
            </Button>
          )}

          {sellOrder?.estado !== SellOrderStatus.IN_COMMUNICATION &&
            sellOrder?.estado !== SellOrderStatus.NEGOTIATION && (
              <Button
                disabled={negotiateDisabled}
                paddingX="12px"
                paddingY="4px"
                bgColor={AppColors.BLACK_TEXT_SECONDARY}
                onClick={handleSendNegotiation}
                loading={loadingSendNegotiation}
                data-testid="sell-order-negotiate-btn"
              >
                <Handshake />
                <Text
                  fontWeight={600}
                  fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
                >
                  Negociación
                </Text>
              </Button>
            )}

          <Button
            disabled={
              loadingSellOrder ||
              loadingRejectSellOrder ||
              loadingSendNegotiation ||
              loadingSendCommunication ||
              loadingExecuteSellOrder ||
              cantOperateSellOrder
            }
            paddingX="12px"
            paddingY="4px"
            bgColor={AppColors.RED_ICON}
            onClick={() => setOpenRejectModal(true)}
            data-testid="sell-order-reject-btn"
          >
            <X />
            <Text
              fontWeight={600}
              fontSize={{ base: "12px", md: "12px", "2xl": "16px" }}
            >
              Rechazar
            </Text>
          </Button>
        </HStack>
      )}
    </Flex>
  );
};
